import { useState, useContext } from "react";
import { Form, Button, FloatingLabel, Spinner } from "react-bootstrap";
import { Navigate, Link } from "react-router-dom";
import UserContext from "../context/UserContext";
import { Notyf } from "notyf";

export default function Register() {
  const notyf = new Notyf();
  const { user } = useContext(UserContext);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [redirectToLogin, setRedirectToLogin] = useState(false);

  const isActive =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    email.trim() !== "" &&
    mobileNo.length === 11 &&
    password.length >= 8 &&
    confirmPassword !== "" &&
    password === confirmPassword;

  const registerUser = async (e) => {
    e.preventDefault();

    if (!isActive || loading) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            mobileNo,
            password,
          }),
        },
      );

      const data = await response.json();

      if (data.message === "Registered successfully") {
        setFirstName("");
        setLastName("");
        setEmail("");
        setMobileNo("");
        setPassword("");
        setConfirmPassword("");

        notyf.success("Registration successful!");

        setRedirectToLogin(true);
      } else if (data.message === "Email invalid") {
        notyf.error("Email is invalid.");
      } else if (data.message === "Mobile number is invalid") {
        notyf.error("Mobile number is invalid.");
      } else if (
        data.message === "Password must be at least 8 characters long"
      ) {
        notyf.error("Password must be at least 8 characters.");
      } else {
        notyf.error(data.message || "Something went wrong.");
      }
    } catch (error) {
      console.error("Registration error:", error);
      notyf.error("Unable to register. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (user.id !== null) {
    return <Navigate to="/products" />;
  }

  if (redirectToLogin) {
    return <Navigate to="/login" />;
  }

  return (
    <section className="auth-section">
      <div className="container">
        <div className="form-container register-container">
          {/* Heading */}
          <div className="auth-heading text-center">
            <span className="section-eyebrow">JOIN DESSERT HAVEN</span>

            <h1>Create your account</h1>

            <p>
              Sign up to order your favorite desserts and keep track of your
              orders.
            </p>
          </div>

          <Form onSubmit={registerUser}>
            {/* First Name */}
            <FloatingLabel
              controlId="register-first-name"
              label="First Name"
              className="mb-3"
            >
              <Form.Control
                type="text"
                placeholder="Enter first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading}
                required
              />
            </FloatingLabel>

            {/* Last Name */}
            <FloatingLabel
              controlId="register-last-name"
              label="Last Name"
              className="mb-3"
            >
              <Form.Control
                type="text"
                placeholder="Enter last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
                required
              />
            </FloatingLabel>

            {/* Email */}
            <FloatingLabel
              controlId="register-email"
              label="Email address"
              className="mb-3"
            >
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </FloatingLabel>

            {/* Mobile Number */}
            <FloatingLabel
              controlId="register-mobile"
              label="Mobile Number"
              className="mb-3"
            >
              <Form.Control
                type="tel"
                placeholder="Enter 11-digit number"
                value={mobileNo}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setMobileNo(value.slice(0, 11));
                }}
                disabled={loading}
                maxLength={11}
                required
              />
            </FloatingLabel>

            {/* Password */}
            <FloatingLabel
              controlId="register-password"
              label="Password"
              className="mb-3"
            >
              <Form.Control
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                minLength={8}
                required
              />
            </FloatingLabel>

            {/* Confirm Password */}
            <FloatingLabel
              controlId="register-confirm-password"
              label="Confirm Password"
              className="mb-3"
            >
              <Form.Control
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                required
                isInvalid={
                  confirmPassword !== "" && password !== confirmPassword
                }
              />

              {confirmPassword !== "" && password !== confirmPassword && (
                <Form.Control.Feedback type="invalid">
                  Passwords do not match.
                </Form.Control.Feedback>
              )}
            </FloatingLabel>

            {/* Register Button */}
            <Button
              type="submit"
              className="dh-button auth-submit-button"
              disabled={!isActive || loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>

            {/* Login */}
            <div className="auth-footer text-center">
              <p>
                Already have an account? <Link to="/login">Login here</Link>
              </p>
            </div>
          </Form>
        </div>
      </div>
    </section>
  );
}
